import { useToast, useTranslations } from "@jugl-web/utils";
import React, { useCallback, useEffect, useMemo } from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useSearchParams } from "react-router-dom";
import { environment } from "@web-src/environments/environment";
import { InstagramAccountContext } from "./InstagramAccountContext";
import { useManageAccountsDrawer } from "../../components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { extractNonEmptyAccount } from "../../utils/extractNonEmptyAccount";

export const InstagramAccountProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { integrationsApi } = useRestApiProvider();
  const { crmConfig, isCRMConfigLoading, isCRMConfigError, refetchCRMConfig } =
    useManageAccountsDrawer();
  const { entityId } = useEntitySelectedProvider();
  const { toast } = useToast();
  const { t } = useTranslations();
  const [params, setParams] = useSearchParams();
  const code = params.get("code");
  const redirectUrl = `${window.location.origin}/instagram-login`;

  const instagramAccount = useMemo(
    () => extractNonEmptyAccount(crmConfig?.instagram),
    [crmConfig?.instagram]
  );

  const [
    connectInstagramAccount,
    { isLoading: isConnectInstagramAccountLoading },
  ] = integrationsApi.useConnectInstagramAccountMutation();

  const isConnected = useMemo(
    () => instagramAccount?.status === "connected",
    [instagramAccount?.status]
  );

  const loginInstagramAccount = useCallback(
    () =>
      window.open(
        `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${environment.instagramClientId}&redirect_uri=${redirectUrl}&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_content_publish%2Cinstagram_business_manage_insights`,
        "_blank"
      ),
    [redirectUrl]
  );

  const handleConnectInstagramAccount = useCallback(
    async (instagramCode: string) => {
      const response = await connectInstagramAccount({
        entityId,
        data: {
          redirect_uri: redirectUrl,
          code: instagramCode,
        },
        meta: {
          onError: (error) => {
            if (
              error.type === "http" &&
              error.serverMessage === "account already exists"
            ) {
              toast(
                t({
                  id: "board-automation-page.account-is-already-connected-in-anotherr-entity",
                  defaultMessage:
                    "Account is already connected in another entity",
                }),
                { variant: "error" }
              );
              return undefined;
            }
            return "unhandled";
          },
        },
      });
      if (response && "data" in response) {
        refetchCRMConfig();
      }
    },
    [connectInstagramAccount, entityId, redirectUrl, toast, t, refetchCRMConfig]
  );

  useEffect(() => {
    if (code && !isCRMConfigLoading && !isCRMConfigError) {
      if (!instagramAccount || instagramAccount.status === "disconnected") {
        handleConnectInstagramAccount(code);
      }
      params.delete("code");
      setParams(params);
    }
  }, [
    code,
    handleConnectInstagramAccount,
    instagramAccount,
    isCRMConfigError,
    isCRMConfigLoading,
    params,
    setParams,
  ]);

  const contextValue = useMemo(
    () => ({
      instagramAccount,
      isConnected,
      isConnectingInstagramAccount:
        isConnectInstagramAccountLoading || (!!code && !isCRMConfigError),
      loginInstagramAccount,
    }),
    [
      instagramAccount,
      isConnected,
      isConnectInstagramAccountLoading,
      code,
      isCRMConfigError,
      loginInstagramAccount,
    ]
  );

  return (
    <InstagramAccountContext.Provider value={contextValue}>
      {children}
    </InstagramAccountContext.Provider>
  );
};
